/**
 * @fileoverview Search and browse utilities for the artwork catalog.
 * @module ui-artwork/catalogQuery
 *
 * Responsibilities: Expose catalog listing and ID/keyword/style/category queries.
 * Dependencies: catalog registry and artwork contracts.
 * Architecture: Pure deterministic catalog projections.
 * Notes: Search matches canonical IDs, aliases, labels, and keywords.
 */
import { ARTWORK_CATALOG, getArtworkEntry } from './catalog';
import type { ArtworkCatalogEntry, ArtworkCategory, ArtworkStyle } from './types';

export interface ArtworkSearchOptions {
  query?: string;
  style?: ArtworkStyle | 'all';
  category?: ArtworkCategory | 'all';
}

/** @reusable listArtwork */
export function listArtwork(): ArtworkCatalogEntry[] {
  return [...ARTWORK_CATALOG];
}

/** @reusable searchArtwork */
export function searchArtwork({ query = '', style = 'all', category = 'all' }: ArtworkSearchOptions = {}): ArtworkCatalogEntry[] {
  const terms = query.trim().toLowerCase();
  return ARTWORK_CATALOG.filter((entry) => {
    if (style !== 'all' && entry.style !== style) return false;
    if (category !== 'all' && entry.category !== category) return false;
    if (!terms) return true;
    return [
      entry.id,
      ...(entry.legacyAliases ?? []),
      entry.label,
      ...entry.keywords,
    ].some((value) => value.toLowerCase().includes(terms));
  });
}

export { getArtworkEntry };
